import { z } from 'zod';

/**
 * §9.2.6 sekme 4 — Kargo fiyatlarının Excel/CSV ile aktarımı.
 *
 * Neden gerekli: depo panelinden çekim yavaş ve dış bir sisteme bağlı.
 * Kullanıcının elinde başka bir kaynaktan gelmiş fiyatlar olabilir, ya da
 * çektiklerini yedeklemek isteyebilir. Bu yol panele hiç dokunmadan çalışır.
 */

/** Dosyadaki bir satır. Kolonlar: `asin, cost_usd[, carrier, service]`. */
export const cargoImportRowSchema = z.object({
  asin: z
    .string({ error: 'ASIN boş' })
    .trim()
    .toUpperCase()
    // Kitaplarda ASIN, ISBN-10'un kendisi (rakamla başlar); `utils/normalize.ts` ile aynı kural
    .regex(/^(B[0-9A-Z]{9}|[0-9]{9}[0-9X])$/i, 'Geçerli bir ASIN değil'),
  costUsd: z
    .number({ error: 'Kargo ücreti okunamadı ya da belirsiz: 20.35 veya 20,35 gibi yazın' })
    .gt(0, 'Kargo ücreti sıfırdan büyük olmalı')
    .max(100_000, "Kargo ücreti 100.000 USD'den büyük olamaz"),
  carrier: z.string().trim().max(100, 'Taşıyıcı adı çok uzun').nullable().default(null),
  service: z.string().trim().max(150, 'Servis adı çok uzun').nullable().default(null),
});
export type CargoImportRow = z.infer<typeof cargoImportRowSchema>;

export const cargoImportSchema = z.object({
  /** Ham dosya içeriği (CSV veya yapıştırılmış tablo). */
  raw: z.string().min(1).max(20_000_000),
  /**
   * Var olan kaydın üzerine yazılsın mı?
   *
   * Varsayılan **hayır**: kargo kayıtları kilitli ve kalıcıdır (§9.2.1).
   * Yanlışlıkla bir dosya yükleyip 80.000 ürünün gerçek fiyatını ezmek,
   * geri alınması zor bir hata olurdu.
   */
  overwrite: z.boolean().default(false),
  /** Yalnızca doğrula ve önizle — hiçbir şey kaydetme. */
  dryRun: z.boolean().default(true),
});
export type CargoImportInput = z.input<typeof cargoImportSchema>;

/** Excel/CSV şablonunun başlık satırı. */
export const CARGO_IMPORT_HEADERS = ['asin', 'cost_usd', 'carrier', 'service'] as const;

/** Başlık adı → alan. Kullanıcı Türkçe veya İngilizce başlık kullanabilir. */
const HEADER_ALIASES: Record<string, keyof CargoImportRow> = {
  asin: 'asin',
  cost_usd: 'costUsd',
  costusd: 'costUsd',
  cost: 'costUsd',
  fiyat: 'costUsd',
  ucret: 'costUsd',
  ücret: 'costUsd',
  kargo: 'costUsd',
  'kargo ücreti': 'costUsd',
  'kargo fiyatı': 'costUsd',
  'kargo (usd)': 'costUsd',
  'ücret (usd)': 'costUsd',
  'fiyat (usd)': 'costUsd',
  carrier: 'carrier',
  tasiyici: 'carrier',
  taşıyıcı: 'carrier',
  service: 'service',
  servis: 'service',
  hizmet: 'service',
};

export interface ParsedImport {
  rows: CargoImportRow[];
  /** Satır numarası (1'den, başlık hariç) → hata. */
  errors: Array<{ line: number; message: string; raw: string }>;
  /** Dosyada kaç satır vardı? */
  totalLines: number;
}

/**
 * CSV / sekmeyle ayrılmış metni satırlara çevirir.
 *
 * Excel'den kopyala-yapıştır sekme ayırıcı üretir; dosya olarak kaydedilince
 * virgül veya noktalı virgül olur. Üçünü de kabul ediyoruz — kullanıcı hangi
 * yolu seçtiyse çalışsın.
 *
 * Hatalı satırlar **atlanmaz, raporlanır**: sessizce eksik içe aktarma,
 * kullanıcının fark etmeyeceği bir veri kaybıdır.
 */
export function parseCargoImport(raw: string): ParsedImport {
  const lines = raw.split(/\r?\n/).filter((line) => line.trim() !== '');
  if (lines.length === 0) return { rows: [], errors: [], totalLines: 0 };

  const delimiter = detectDelimiter(lines[0]!);
  const header = splitLine(lines[0]!, delimiter).map((h) =>
    h
      .trim()
      .toLowerCase()
      // "İ" küçültülünce üstünde birleşen nokta kalıyor ("KARGO ÜCRETİ")
      .replace(/\u0307/g, '')
      .replace(/^["']|["']$/g, ''),
  );

  // Başlık satırı var mı? Yoksa ilk satır da veri sayılır.
  const hasHeader = header.some((h) => h in HEADER_ALIASES);
  const columns = hasHeader
    ? header.map((h) => HEADER_ALIASES[h] ?? null)
    : (['asin', 'costUsd', 'carrier', 'service'] as Array<keyof CargoImportRow | null>);

  // Sütun tanınmadıysa her satır aynı hatayı vermesin: sebep tek cümle
  const eksikSutun = !hasHeader
    ? null
    : !columns.includes('asin')
      ? 'asin'
      : !columns.includes('costUsd')
        ? 'cost_usd'
        : null;
  if (eksikSutun !== null) {
    return {
      rows: [],
      errors: [
        {
          line: 1,
          message: `Başlık satırında "${eksikSutun}" sütunu yok. Beklenen sütunlar: ${CARGO_IMPORT_HEADERS.join(', ')}`,
          raw: lines[0]!.slice(0, 120),
        },
      ],
      totalLines: lines.length - 1,
    };
  }

  const rows: CargoImportRow[] = [];
  const errors: ParsedImport['errors'] = [];
  const seen = new Set<string>();

  for (let i = hasHeader ? 1 : 0; i < lines.length; i++) {
    const line = lines[i]!;
    const cells = splitLine(line, delimiter);

    const record: Record<string, unknown> = {};
    for (let c = 0; c < columns.length && c < cells.length; c++) {
      const field = columns[c];
      if (field === null || field === undefined) continue;
      const value = cells[c]!.trim().replace(/^["']|["']$/g, '');
      record[field] = field === 'costUsd' ? parsePrice(value) : value === '' ? null : value;
    }

    /*
     * ⚠️ Virgülle ayrılmış dosyada ondalık virgül sütuna dönüşür: `B0X,24,50` ücreti 24, taşıyıcıyı
     * "50" okurdu ve satır geçerli görünürdü (doğrudan eklemede 2 Eki 2026'da yaşandı: 41.511 satır).
     * Başlıktan fazla hücre ya da sayıdan ibaret taşıyıcı bu karışmanın izi.
     */
    const fazlaHucre = cells.slice(columns.length).some((h) => h.trim() !== '');
    if (
      delimiter === ',' &&
      (fazlaHucre || (typeof record.carrier === 'string' && /^\d+$/.test(record.carrier)))
    ) {
      errors.push({
        line: i + 1,
        message:
          'Ondalık virgül sütun ayırıcıyla karışmış olabilir: ücreti 20.35 gibi noktayla ya da tırnak içinde yazın',
        raw: line.slice(0, 120),
      });
      continue;
    }

    const parsed = cargoImportRowSchema.safeParse(record);
    if (!parsed.success) {
      errors.push({
        line: i + 1,
        message: parsed.error.issues[0]?.message ?? 'Satır okunamadı',
        raw: line.slice(0, 120),
      });
      continue;
    }

    // Aynı ASIN iki kez varsa sonuncusu kazanır ama kullanıcı bilsin
    if (seen.has(parsed.data.asin)) {
      errors.push({
        line: i + 1,
        message: `${parsed.data.asin} dosyada birden çok kez var; sonuncusu kullanılacak`,
        raw: line.slice(0, 120),
      });
      const index = rows.findIndex((r) => r.asin === parsed.data.asin);
      if (index !== -1) rows.splice(index, 1);
    }

    seen.add(parsed.data.asin);
    rows.push(parsed.data);
  }

  return { rows, errors, totalLines: lines.length - (hasHeader ? 1 : 0) };
}

function detectDelimiter(line: string): string {
  if (line.includes('\t')) return '\t';
  if (line.includes(';')) return ';';
  return ',';
}

/** Tırnak içindeki ayırıcı hücreyi bölmez: `B0X,"24,50",FedEx` üç hücredir. */
function splitLine(line: string, delimiter: string): string[] {
  if (!line.includes('"')) return line.split(delimiter);

  const hucreler: string[] = [];
  let hucre = '';
  let tirnakta = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (c === '"' && (tirnakta || hucre.trim() === '')) {
      if (tirnakta && line[i + 1] === '"') {
        hucre += '"';
        i++;
      } else {
        tirnakta = !tirnakta;
      }
    } else if (c === delimiter && !tirnakta) {
      hucreler.push(hucre);
      hucre = '';
    } else {
      hucre += c;
    }
  }
  hucreler.push(hucre);
  return hucreler;
}

/**
 * "24,50" · "$24.50" · "24.50 USD" → 24.5
 *
 * Kullanıcının dosyası Excel'in yerel ayarına göre virgüllü ondalık içerebilir.
 * Yanlış okumak fiyatı 100 kat değiştirebilir, bu yüzden belirsizse `NaN`
 * dönüp satırı hataya düşürüyoruz — tahmin etmiyoruz.
 */
export function parsePrice(raw: string): number {
  const cleaned = raw.replace(/[^\d.,-]/g, '').trim();
  if (cleaned === '') return Number.NaN;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');

  let normalized = cleaned;
  if (lastDot !== -1 && lastComma !== -1) {
    normalized =
      lastComma > lastDot ? cleaned.replace(/\./g, '').replace(',', '.') : cleaned.replace(/,/g, '');
  } else if (lastComma !== -1) {
    /*
     * Virgülden sonra tam 3 hane BELİRSİZ: "1,234" binlik de olabilir, Türkçe Excel'den gelen
     * "22,385" ondalık da. Binlik sayılırsa 22,39 USD'lik kargo 22.385 USD olarak kilitlenirdi.
     */
    if (cleaned.length - lastComma - 1 === 3 || cleaned.indexOf(',') !== lastComma) {
      return Number.NaN;
    }
    normalized = cleaned.replace(',', '.');
  } else if (cleaned.indexOf('.') !== lastDot) {
    return Number.NaN;
  }

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}
